/**
 * 项目管理 API
 */
import type { Project, Asset, CanvasPreferences, SaveStateRequest } from '@/types/editor';
import type {
  ApiResponse,
  PaginatedResponse,
  SaveStateResponse,
  VersionConflictError,
  ProjectHistoryItem,
  HistoryVersionDetail,
} from './types';
import { ApiClient } from './client';
import { buildVideoSnapshotUrl, isVideoFileUrl } from '@/lib/task-media';
import { toDisplayMediaUrl, unwrapNextImageOptimizationUrl } from '@/lib/media-url';

export interface ProjectApiRecord {
  id: string;
  name: string;
  description?: string | null;
  category_id?: string | null;
  project_kind?: 'canvas' | 'director_board';
  user_id?: string;
  status?: string;
  canvas_preferences?: CanvasPreferences | null;
  resolution?: { width: number; height: number } | null;
  fps?: number | null;
  duration?: number;
  thumbnail_url?: string | null;
  thumbnail_asset_id?: string | null;
  updated_at: string;
  created_at?: string;
  node_count?: number;
  wizard_completed?: boolean;
  scene_count?: number;
  last_scene_id?: string | null;
  last_scene_title?: string | null;
  last_render_url?: string | null;
  active_task_count?: number;
  role?: 'owner' | 'editor' | 'viewer';
}

export interface CollaboratedProjectApiRecord extends ProjectApiRecord {
  role: 'editor' | 'viewer';
}

export interface ProjectCategoryApiRecord {
  id: string;
  name: string;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

export interface ProjectTrashItem extends ProjectApiRecord {
  deleted_at: string;
  expires_at: string;
}

export interface ProjectQuotaStatus {
  current_count: number;
  max_projects: number;
  plan_slug: string;
  locked: boolean;
}

export interface ProjectItemQuotaStatus {
  project_id: string;
  current_count: number;
  max_items_per_project: number | null;
  plan_slug: string;
  locked: boolean;
}

export interface ProjectRunTask {
  id: string;
  type?: string | null;
  status: string;
  progress?: number | null;
  current_step?: string | null;
  error_message?: string | null;
  credits_consumed?: number | null;
  output_url?: string | null;
  thumbnail_url?: string | null;
  created_at?: string | null;
  completed_at?: string | null;
  updated_at?: string | null;
}

export type GenerationRunsPage = Omit<PaginatedResponse<ProjectRun>, 'total'> & {
  total: number | null;
  has_more?: boolean;
  next_offset?: number | null;
};

export interface ProjectRun {
  id: string;
  source: 'chat_message' | 'task';
  thread_id?: string | null;
  thread_title?: string | null;
  message_id?: string | null;
  created_at?: string | null;
  content: string;
  pending_plan?: Record<string, unknown> | null;
  generated_results: Array<Record<string, unknown>>;
  preview_url?: string | null;
  tasks: ProjectRunTask[];
  task_ids: string[];
  task_count: number;
  status: 'pending_plan' | 'message' | 'running' | 'completed' | 'failed' | 'partial';
  credits_consumed: number;
}

function normalizeProjectRun(run: ProjectRun): ProjectRun {
  return {
    ...run,
    preview_url: run.preview_url ? toDisplayMediaUrl(run.preview_url) : run.preview_url,
    generated_results: run.generated_results.map((result) => {
      const next = { ...result };
      for (const key of ['previewUrl', 'thumbnailUrl'] as const) {
        if (typeof next[key] === 'string') next[key] = toDisplayMediaUrl(next[key] as string);
      }
      if (Array.isArray(next.candidateImages)) {
        next.candidateImages = next.candidateImages.map((candidate) => (
          candidate && typeof candidate === 'object' && typeof (candidate as { url?: unknown }).url === 'string'
            ? { ...candidate, url: toDisplayMediaUrl((candidate as { url: string }).url) }
            : candidate
        ));
      }
      return next;
    }),
    tasks: run.tasks.map((task) => ({
      ...task,
      output_url: task.output_url ? toDisplayMediaUrl(task.output_url) : task.output_url,
      thumbnail_url: task.thumbnail_url ? toDisplayMediaUrl(task.thumbnail_url) : task.thumbnail_url,
    })),
  };
}

function normalizeProjectThumbnailUrl(thumbnailUrl?: string | null): string | null | undefined {
  if (!thumbnailUrl) {
    return thumbnailUrl;
  }
  const normalizedThumbnailUrl = toDisplayMediaUrl(unwrapNextImageOptimizationUrl(thumbnailUrl));
  // A project cover is a tiny display image. Legacy records use the original
  // stream locator, which bypasses resizing and re-downloads megabytes on open.
  // Keep the authenticated delivery boundary and use its persisted thumbnail.
  const assetStream = normalizedThumbnailUrl.match(/^\/api\/auth\/media\/assets\/stream\/([^/?#]+)([?#].*)?$/);
  if (assetStream) {
    return `/api/auth/media/assets/${assetStream[1]}/thumbnail${assetStream[2] || ''}`;
  }
  if (!isVideoFileUrl(normalizedThumbnailUrl)) {
    return normalizedThumbnailUrl;
  }
  return buildVideoSnapshotUrl(normalizedThumbnailUrl) ?? null;
}

function normalizeProjectRecord<T extends ProjectApiRecord>(project: T): T {
  return {
    ...project,
    thumbnail_url: normalizeProjectThumbnailUrl(project.thumbnail_url),
  };
}

function normalizeProjectPage<T extends ProjectApiRecord>(page: PaginatedResponse<T>): PaginatedResponse<T> {
  return {
    ...page,
    items: page.items.map(normalizeProjectRecord),
  };
}

export interface ProjectCreateInput {
  /** A durable entry owns this exact create identity across refresh/unknown ACK. */
  client_request_id?: string;
  name: string;
  description?: string | null;
  category_id?: string | null;
  thumbnail_url?: string | null;
  project_kind?: 'canvas' | 'director_board';
  video_asset_id?: string;
  canvas_preferences?: CanvasPreferences;
  resolution?: { width: number; height: number };
  fps?: number;
  settings?: {
    resolution?: { width: number; height: number };
    fps?: number;
  };
}

const PROJECT_CREATE_INTENTS_STORAGE_KEY = 'dobi:project-create-intents:v1';
let pendingProjectCreateIntents: Map<string, string> | null = null;

function projectCreateIntentSignature(data: ProjectCreateInput): string {
  return JSON.stringify({
    name: data.name,
    description: data.description ?? null,
    category_id: data.category_id ?? null,
    thumbnail_url: data.thumbnail_url ?? null,
    project_kind: data.project_kind ?? 'canvas',
    video_asset_id: data.video_asset_id ?? null,
    canvas_preferences: data.canvas_preferences ?? null,
    resolution: data.resolution ?? null,
    fps: data.fps ?? null,
    settings: data.settings ?? null,
  });
}

function loadPendingProjectCreateIntents(): Map<string, string> {
  if (pendingProjectCreateIntents) return pendingProjectCreateIntents;
  pendingProjectCreateIntents = new Map<string, string>();
  if (typeof window === 'undefined') return pendingProjectCreateIntents;
  try {
    const persisted = JSON.parse(
      window.sessionStorage.getItem(PROJECT_CREATE_INTENTS_STORAGE_KEY) || '[]'
    ) as unknown;
    if (Array.isArray(persisted)) {
      for (const entry of persisted) {
        if (
          Array.isArray(entry)
          && typeof entry[0] === 'string'
          && typeof entry[1] === 'string'
        ) {
          pendingProjectCreateIntents.set(entry[0], entry[1]);
        }
      }
    }
  } catch {
    // In-memory authority still preserves one mounted create intent.
  }
  return pendingProjectCreateIntents;
}

function persistPendingProjectCreateIntents(intents: Map<string, string>): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(
      PROJECT_CREATE_INTENTS_STORAGE_KEY,
      JSON.stringify([...intents.entries()])
    );
  } catch {
    // Storage can be unavailable in private/embedded contexts.
  }
}

function projectCreateClientRequestId(data: ProjectCreateInput): {
  signature: string;
  clientRequestId: string;
} {
  const signature = projectCreateIntentSignature(data);
  if (data.client_request_id) return { signature, clientRequestId: data.client_request_id };
  const intents = loadPendingProjectCreateIntents();
  const existing = intents.get(signature);
  if (existing) return { signature, clientRequestId: existing };
  const clientRequestId = crypto.randomUUID();
  intents.set(signature, clientRequestId);
  persistPendingProjectCreateIntents(intents);
  return { signature, clientRequestId };
}

function clearProjectCreateIntent(signature: string): void {
  const intents = loadPendingProjectCreateIntents();
  intents.delete(signature);
  persistPendingProjectCreateIntents(intents);
}

class ProjectApi extends ApiClient {
  private readonly projectMetaCache = new Map<string, {
    expiresAt: number;
    response: ApiResponse<ProjectApiRecord>;
  }>();

  private readonly projectMetaInFlight = new Map<string, Promise<ApiResponse<ProjectApiRecord>>>();
  private readonly projectMetaEpoch = new Map<string, number>();

  clearProjectMetaCache(projectId?: string): void {
    if (projectId) {
      this.projectMetaEpoch.set(projectId, (this.projectMetaEpoch.get(projectId) ?? 0) + 1);
      this.projectMetaCache.delete(projectId);
      this.projectMetaInFlight.delete(projectId);
      return;
    }
    const knownProjectIds = new Set([
      ...this.projectMetaEpoch.keys(),
      ...this.projectMetaCache.keys(),
      ...this.projectMetaInFlight.keys(),
    ]);
    knownProjectIds.forEach((id) => {
      this.projectMetaEpoch.set(id, (this.projectMetaEpoch.get(id) ?? 0) + 1);
    });
    this.projectMetaCache.clear();
    this.projectMetaInFlight.clear();
  }

  /**
   * 获取项目列表
   */
  async getProjects(params?: {
    limit?: number;
    offset?: number;
    status?: string;
    projectKind?: 'canvas' | 'director_board';
    includeDirectorSummary?: boolean;
  }): Promise<ApiResponse<PaginatedResponse<ProjectApiRecord>>> {
    const searchParams = new URLSearchParams();
    const projectKind = params?.projectKind ?? 'canvas';
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    if (params?.status) searchParams.set('status', params.status);
    searchParams.set('project_kind', projectKind);
    if (params?.includeDirectorSummary) searchParams.set('include_director_summary', 'true');
    
    const query = searchParams.toString();
    const response = await this.request<PaginatedResponse<ProjectApiRecord>>(`/projects${query ? `?${query}` : ''}`);
    if (!response.data) return response;
    return { ...response, data: normalizeProjectPage(response.data) };
  }

  /**
   * 轻量级项目列表 — 仅 id + name，用于选择器
   */
  async getProjectsPicker(limit = 10, projectKind: 'canvas' | 'director_board' = 'canvas'): Promise<ApiResponse<{ items: { id: string; name: string }[] }>> {
    const searchParams = new URLSearchParams({ limit: String(limit) });
    searchParams.set('project_kind', projectKind);
    return this.request(`/projects/picker?${searchParams.toString()}`);
  }

  async getProjectsQuotaStatus(): Promise<ApiResponse<ProjectQuotaStatus>> {
    return this.request('/projects/quota-status');
  }

  async getProjectItemsQuotaStatus(projectId: string): Promise<ApiResponse<ProjectItemQuotaStatus>> {
    return this.request(`/projects/${encodeURIComponent(projectId)}/quota-status`);
  }

  async getProjectCategories(): Promise<ApiResponse<{ items: ProjectCategoryApiRecord[] }>> {
    return this.request('/projects/categories');
  }

  async createProjectCategory(name: string): Promise<ApiResponse<ProjectCategoryApiRecord>> {
    return this.request('/projects/categories', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
  }

  async updateProjectCategory(
    categoryId: string,
    data: { name?: string; sort_order?: number }
  ): Promise<ApiResponse<ProjectCategoryApiRecord>> {
    return this.request(`/projects/categories/${categoryId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteProjectCategory(categoryId: string): Promise<ApiResponse<{ deleted: boolean; category_id: string }>> {
    return this.request(`/projects/categories/${categoryId}`, {
      method: 'DELETE',
    });
  }

  /**
   * 创建项目
   */
  async createProject(data: ProjectCreateInput): Promise<ApiResponse<ProjectApiRecord>> {
    const { signature, clientRequestId } = projectCreateClientRequestId(data);
    const response = await this.request<ProjectApiRecord>('/projects', {
      method: 'POST',
      headers: { 'X-Request-ID': clientRequestId },
      body: JSON.stringify({ ...data, client_request_id: clientRequestId }),
    });
    if (response.data) {
      clearProjectCreateIntent(signature);
      return { ...response, data: normalizeProjectRecord(response.data) };
    }
    if (response.error?.code === 'NETWORK_ERROR') {
      const receipt = await this.request<ProjectApiRecord>(
        `/projects/creation-requests/${encodeURIComponent(clientRequestId)}`,
        { headers: { 'X-Request-ID': clientRequestId } }
      );
      if (receipt.data) {
        clearProjectCreateIntent(signature);
        return { ...receipt, data: normalizeProjectRecord(receipt.data) };
      }
      // Keep the same intent key. A later user retry may safely POST it again;
      // a new project id can never be allocated for this ambiguous create.
      return response;
    }
    clearProjectCreateIntent(signature);
    return response;
  }

  /**
   * 获取项目详情
   */
  async getProjectMeta(
    projectId: string,
    options: { forceRefresh?: boolean } = {},
  ): Promise<ApiResponse<ProjectApiRecord>> {
    if (options.forceRefresh) {
      this.projectMetaEpoch.set(projectId, (this.projectMetaEpoch.get(projectId) ?? 0) + 1);
      this.projectMetaCache.delete(projectId);
      this.projectMetaInFlight.delete(projectId);
    }
    const requestEpoch = this.projectMetaEpoch.get(projectId) ?? 0;

    if (!options.forceRefresh) {
      const cached = this.projectMetaCache.get(projectId);
      if (cached && cached.expiresAt > Date.now()) {
        return cached.response;
      }

      const inFlight = this.projectMetaInFlight.get(projectId);
      if (inFlight) {
        return inFlight;
      }
    }

    // This request gates project navigation. A stalled transport must resolve
    // to the route's retry state instead of leaving access checking forever.
    const request: Promise<ApiResponse<ProjectApiRecord>> = this.request<ProjectApiRecord>(
      `/projects/${projectId}/meta`,
      { signal: AbortSignal.timeout(15_000) },
    )
      .then((response) => {
        const normalized = response.data
          ? { ...response, data: normalizeProjectRecord(response.data) }
          : response;
        const isLatestAccessCheck = (this.projectMetaEpoch.get(projectId) ?? 0) === requestEpoch;
        if (normalized.data && isLatestAccessCheck) {
          // Canvas boot has three independent consumers of the same immutable access/meta row.
          // A short cache removes both overlapping and immediately sequential round-trips.
          this.projectMetaCache.set(projectId, {
            expiresAt: Date.now() + 5_000,
            response: normalized,
          });
        } else if (options.forceRefresh && isLatestAccessCheck) {
          this.projectMetaCache.delete(projectId);
        }
        return normalized;
      })
      .finally(() => {
        if (!options.forceRefresh && this.projectMetaInFlight.get(projectId) === request) {
          this.projectMetaInFlight.delete(projectId);
        }
      });

    if (!options.forceRefresh) {
      this.projectMetaInFlight.set(projectId, request);
    }
    return request;
  }

  /**
   * 获取项目详情
   */
  async getProject(projectId: string): Promise<ApiResponse<Project & { assets: Asset[] }>> {
    return this.request(`/projects/${projectId}`);
  }

  async getProjectRuns(
    projectId: string,
    params?: { limit?: number; offset?: number },
  ): Promise<ApiResponse<PaginatedResponse<ProjectRun>>> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    const query = searchParams.toString();
    const response = await this.request<PaginatedResponse<ProjectRun>>(
      `/projects/${encodeURIComponent(projectId)}/runs${query ? `?${query}` : ''}`,
    );
    if (!response.success || !response.data) return response;
    return {
      ...response,
      data: {
        ...response.data,
        items: response.data.items.map(normalizeProjectRun),
      },
    };
  }

  async getGenerationRuns(
    params?: { limit?: number; offset?: number; q?: string; includeTotal?: boolean },
    signal?: AbortSignal,
  ): Promise<ApiResponse<GenerationRunsPage>> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));
    if (params?.q?.trim()) searchParams.set('q', params.q.trim());
    if (params?.includeTotal === false) searchParams.set('include_total', 'false');
    const query = searchParams.toString();
    const response = await this.request<GenerationRunsPage>(
      `/projects/generation-history/runs${query ? `?${query}` : ''}`,
      { signal },
    );
    if (!response.success || !response.data) return response;
    return {
      ...response,
      data: {
        ...response.data,
        items: response.data.items.map(normalizeProjectRun),
      },
    };
  }

  /**
   * 更新项目
   */
  async updateProject(
    projectId: string,
    data: {
      name?: string;
      description?: string;
      category_id?: string | null;
      canvas_preferences?: CanvasPreferences;
      settings?: Record<string, unknown>;
      status?: string;
      wizard_completed?: boolean;
      project_kind?: 'canvas' | 'director_board';
      resolution?: { width: number; height: number };
      fps?: number;
    }
  ): Promise<ApiResponse<ProjectApiRecord>> {
    const response = await this.request<ProjectApiRecord>(`/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    this.clearProjectMetaCache(projectId);
    if (!response.data) return response;
    return { ...response, data: normalizeProjectRecord(response.data) };
  }

  /**
   * 删除项目
   */
  async deleteProject(projectId: string): Promise<ApiResponse<{ message: string }>> {
    const response = await this.request<{ message: string }>(`/projects/${projectId}`, {
      method: 'DELETE',
    });
    this.clearProjectMetaCache(projectId);
    return response;
  }

  async getTrashItems(params?: {
    limit?: number;
    offset?: number;
  }): Promise<ApiResponse<PaginatedResponse<ProjectTrashItem>>> {
    const searchParams = new URLSearchParams();
    if (params?.limit) searchParams.set('limit', String(params.limit));
    if (params?.offset) searchParams.set('offset', String(params.offset));

    const query = searchParams.toString();
    const response = await this.request<PaginatedResponse<ProjectTrashItem>>(`/projects/trash/items${query ? `?${query}` : ''}`);
    if (!response.data) return response;
    return { ...response, data: normalizeProjectPage(response.data) };
  }

  async restoreFromTrash(projectIds: string[]): Promise<ApiResponse<{ restored: number; restored_ids: string[] }>> {
    return this.request('/projects/trash/restore', {
      method: 'POST',
      body: JSON.stringify({ project_ids: projectIds }),
    });
  }

  async permanentlyDelete(projectIds: string[]): Promise<ApiResponse<{ deleted: number; deleted_ids: string[] }>> {
    return this.request('/projects/trash/permanent-delete', {
      method: 'POST',
      body: JSON.stringify({ project_ids: projectIds }),
    });
  }

  /**
   * 保存项目状态（核心接口）
   */
  async saveProjectState(
    projectId: string,
    request: SaveStateRequest
  ): Promise<ApiResponse<SaveStateResponse | VersionConflictError>> {
    return this.request(`/projects/${projectId}/state`, {
      method: 'PATCH',
      body: JSON.stringify(request),
    });
  }

  /**
   * 获取项目历史版本列表
   */
  async getProjectHistory(
    projectId: string,
    limit: number = 20
  ): Promise<ApiResponse<{ snapshots: ProjectHistoryItem[] }>> {
    return this.request(`/projects/${projectId}/history?limit=${limit}`);
  }

  /**
   * 获取指定历史版本详情
   */
  async getHistoryVersion(
    projectId: string,
    version: number
  ): Promise<ApiResponse<HistoryVersionDetail>> {
    return this.request(`/projects/${projectId}/history/${version}`);
  }

  /**
   * 恢复到指定历史版本
   */
  async restoreVersion(
    projectId: string,
    version: number
  ): Promise<ApiResponse<{ message: string; new_version: number }>> {
    return this.request(`/projects/${projectId}/restore/${version}`, {
      method: 'POST',
    });
  }

  /**
   * 获取参与协作的项目列表（role != owner）
   */
  async getCollaboratedProjects(): Promise<ApiResponse<{ items: CollaboratedProjectApiRecord[] }>> {
    const response = await this.request<{ items: CollaboratedProjectApiRecord[] }>('/projects/collaborated');
    if (!response.data) return response;
    return {
      ...response,
      data: {
        ...response.data,
        items: response.data.items.map(normalizeProjectRecord),
      },
    };
  }
}

export const projectApi = new ProjectApi();
